import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { GTMTracking } from '@common/ui/general';
import { gtmEventHandler } from '@common/utils/gtm';

import closeIcon from '@alumni_v2/images/svg/cross-icon.svg';
import searchIcon from '@alumni_v2/images/svg/search-icon.svg';

import { updateRequestParams } from '@alumni_v2/stores/reviews/actions';
import { getPreSelectedFilters } from '@alumni_v2/utils/getPreSelectedFilters';
import {
  allowedAdvancedFilters,
  AdvancedfiltersNameMapping,
  defaultAdvancedOptions,
} from '@alumni_v2/utils/constants';
import {
  createAdvancedFiltersQuery,
  getFilteredOptions,
} from '@alumni_v2/utils/filters';
import AdvancedFiltersOptions from './AdvancedFiltersOptions';
import { ExperienceFilter, SalaryHikeFilter } from '../RangeFilters';

import styles from './AdvancedFiltersMobile.module.scss';

const AdvancedFiltersMobile = ({
  isPopUpVisible,
  setIsPopUpVisible,
  experienceFilter,
  setExperienceFilter,
  salaryHikeFilter,
  setSalaryHikeFilter,
  selectedFilters,
  setSelectedFilters,
}) => {
  const dispatch = useDispatch();
  const { filterOptions } = window.__INITIAL_STATE__;
  const [
    selectedFilterType, setSelectedFilterType] = useState('pre_scaler_role');
  const [searchTerms, setSearchTerms] = useState({});

  const preScalerRoleOptions = useMemo(
    () => getFilteredOptions(filterOptions.pre_scaler_role || [], [
      'Student/Fresher',
      'Non Tech',
    ]),
    [filterOptions.pre_scaler_role],
  );

  const targetRoleOptions = useMemo(
    () => getFilteredOptions(filterOptions.role || [], ['SDE1']),
    [filterOptions.role],
  );

  const preScalerCompanyOptions = useMemo(
    () => getFilteredOptions(filterOptions.pre_academy_company || [], ['TCS']),
    [filterOptions.pre_academy_company],
  );

  const targetCompanyOptions = useMemo(
    () => getFilteredOptions(filterOptions.post_academy_segment || [], [
      'High Growth Startups',
      'Established Businesses',
      'Tech Giants',
    ]),
    [filterOptions.post_academy_segment],
  );

  const collegeOptions = useMemo(
    () => getFilteredOptions(filterOptions.college || [], [
      'VIT Vellore',
      'BITS Pilani',
    ]),
    [filterOptions.college],
  );

  const allFilterOptions = {
    college: [...collegeOptions, ...defaultAdvancedOptions.college],
    pre_scaler_role: [
      ...preScalerRoleOptions,
      ...defaultAdvancedOptions.pre_scaler_role,
    ],
    role: [...targetRoleOptions, ...defaultAdvancedOptions.role],
    pre_academy_company: [
      ...preScalerCompanyOptions,
      ...defaultAdvancedOptions.pre_academy_company],
    post_academy_segment: [
      ...targetCompanyOptions,
      ...defaultAdvancedOptions.post_academy_segment,
    ],
  };

  const filtersToShow = useMemo(() => {
    const roleBasedFilter = selectedFilters
      .pre_scaler_role?.includes('Student/Fresher')
      ? { college: filterOptions.college }
      : { pre_academy_company: filterOptions.pre_academy_company };

    const filteredAllowedFilters = allowedAdvancedFilters.filter(
      (key) => key !== 'college' && key !== 'pre_academy_company',
    );

    const filters = filteredAllowedFilters.reduce((acc, key) => {
      if (filterOptions[key]) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {});

    const combinedFilters = { ...filters, ...roleBasedFilter };

    const sortedFilters = allowedAdvancedFilters.reduce((acc, key) => {
      if (combinedFilters[key]) {
        acc[key] = combinedFilters[key];
      }
      return acc;
    }, {});
    return sortedFilters;
  }, [filterOptions, selectedFilters]);

  const togglePopUp = useCallback(() => {
    setIsPopUpVisible((prevState) => !prevState);
  }, [setIsPopUpVisible]);

  const handleFilterSelect = useCallback((filterType) => {
    setSelectedFilterType(filterType);
    setSelectedFilters((prevSelectedFilters) => ({
      ...prevSelectedFilters,
      [filterType]: prevSelectedFilters[filterType] || [],
    }));
  }, [setSelectedFilters]);

  const handleSearchChange = useCallback(
    (e) => {
      if (!e.target) return;
      const { value } = e.target;
      if (!selectedFilterType) return;
      setSearchTerms((prevSearchTerms) => ({
        ...prevSearchTerms,
        [selectedFilterType]: value,
      }));
    },
    [selectedFilterType],
  );

  const filteredOptions = allFilterOptions[selectedFilterType]?.filter(
    (option) => !searchTerms[selectedFilterType]
      || option
        .toLowerCase()
        .includes(searchTerms[selectedFilterType].toLowerCase())
  );

  const searchAlumniHandler = useCallback(() => {
    togglePopUp();
    const { preSelectedFiltersJson, sharedFilter } = getPreSelectedFilters();

    const query = createAdvancedFiltersQuery(selectedFilters);

    gtmEventHandler('cta', 'apply_filter', 'click', 'alumni_directory', {
      click_type: 'apply_filter',
      click_section: 'filter_section',
      click_text: 'Search Alumni',
      gtm_custom_attributes: {
        current_role:
        selectedFilters.pre_scaler_role
          ? selectedFilters.pre_scaler_role[0] : null,
        current_company: selectedFilters.pre_academy_company
          ? selectedFilters.pre_academy_company[0] : null,
        target_role: selectedFilters.role
          ? selectedFilters.role[0] : null,
        target_company: selectedFilters.post_academy_segment
          ? selectedFilters.post_academy_segment[0] : null,
        college: selectedFilters.college
          ? selectedFilters.college[0] : null,
      },
    });

    dispatch(
      updateRequestParams(
        preSelectedFiltersJson,
        { ...sharedFilter, ...query },
        null,
        true,
        true,
      ),
    );
  }, [dispatch, selectedFilters, togglePopUp]);

  const dropDownOptions = useCallback(() => (
    <AdvancedFiltersOptions
      searchTerms={searchTerms}
      selectedFilters={selectedFilters}
      selectedFilterType={selectedFilterType}
      filteredOptions={filteredOptions}
      allFilterOptions={allFilterOptions}
      setSearchTerms={setSearchTerms}
      setSelectedFilters={setSelectedFilters}
    />
  ), [
    searchTerms, selectedFilters, selectedFilterType,
    filteredOptions, allFilterOptions, setSelectedFilters]);

  const rightSectionUI = useCallback(() => {
    if (selectedFilterType === 'experience') {
      return (
        <ExperienceFilter
          experienceFilter={experienceFilter}
          setExperienceFilter={setExperienceFilter}
        />
      );
    } else if (selectedFilterType === 'growth') {
      return (
        <SalaryHikeFilter
          salaryHikeFilter={salaryHikeFilter}
          setSalaryHikeFilter={setSalaryHikeFilter}
        />
      );
    }
    return (
      <>
        <div className={styles.searchBarContainer}>
          <input
            className={styles.searchBar}
            type="text"
            placeholder="Search"
            value={searchTerms[selectedFilterType] || ''}
            onChange={handleSearchChange}
          />
          <img
            src={searchIcon}
            alt="Search Icon"
            height={16}
            width={16}
            className={styles.searchIcon}
          />
        </div>
        <div className={styles.filterOptions}>
          {dropDownOptions()}
        </div>
      </>
    );
  }, [
    selectedFilterType, searchTerms, handleSearchChange, dropDownOptions,
    experienceFilter, setExperienceFilter, salaryHikeFilter,
    setSalaryHikeFilter]);

  const handleClearAll = useCallback(() => {
    togglePopUp();
    setSelectedFilters({});
    setSearchTerms({});
    setSalaryHikeFilter({ min: 0, max: 900 });
    setExperienceFilter({ min: 2, max: 5 });
    dispatch(
      updateRequestParams(
        [], {}, null, true,
      ),
    );
  }, [dispatch, setExperienceFilter, setSalaryHikeFilter,
    setSelectedFilters, togglePopUp]);

  if (!isPopUpVisible) return null;

  return (
    <div className={styles.advancedFilterPopup}>
      <div className={styles.popUpHeader}>
        <div className={styles.popUpheading}>Filters</div>
        <GTMTracking
          className={styles.popUpCloseButton}
          onClick={togglePopUp}
          component="div"
          gtmEventType="cta"
          gtmEventAction="click"
          gtmEventResult="close_filter_pane"
          gtmEventCategory="alumni_directory"
          gtmEventData={{
            click_type: 'close_filter_pane',
            click_section: 'filter_section',
          }}
        >
          <img src={closeIcon} alt="closeIcon" height={16} width={16} />
        </GTMTracking>
      </div>

      <div className={styles.filterSection}>
        <div className={styles.leftSection}>
          {Object.keys(filtersToShow).map((filterType) => (
            <GTMTracking
              component="div"
              key={filterType}
              className={`${styles.filterNameWrapper}
                ${
            selectedFilterType === filterType ? styles.selectedFilter : ''
            }`}
              onClick={() => handleFilterSelect(filterType)}
              gtmEventType="cta"
              gtmEventAction="click"
              gtmEventResult="open_filter_dropdown"
              gtmEventCategory="alumni_directory"
              gtmEventData={{
                click_type: 'open_filter_dropdown',
                click_text: AdvancedfiltersNameMapping[filterType],
                click_section: 'filter_section',
              }}
            >
              <div className={styles.filterName}>
                {AdvancedfiltersNameMapping[filterType]}
              </div>
            </GTMTracking>
          ))}
        </div>

        <div className={styles.rightSection}>
          <div className={styles.rightSectionContent}>
            {rightSectionUI()}
          </div>
        </div>
      </div>

      <div className={styles.popUpBottom}>
        <GTMTracking
          className={styles.clearAllFilter}
          component="button"
          gtmEventType="cta"
          gtmEventAction="click"
          gtmEventResult="clear_all_filter"
          gtmEventCategory="alumni_directory"
          gtmEventData={{
            click_type: 'clear_all_filter',
            click_text: 'Clear All',
            click_section: 'filter_section',
          }}
          onClick={handleClearAll}
        >
          Clear All
        </GTMTracking>
        <GTMTracking
          className={styles.applyFilters}
          component="button"
          gtmEventType="cta"
          gtmEventAction="click"
          gtmEventResult="apply_filter"
          gtmEventCategory="alumni_directory"
          gtmEventData={{
            click_type: 'apply_filter',
            click_text: 'Apply',
            click_section: 'filter_section',
          }}
          onClick={searchAlumniHandler}
        >
          Apply
        </GTMTracking>
      </div>
    </div>
  );
};

export default AdvancedFiltersMobile;
